import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Megaphone, ChevronRight, Copy, Check } from 'lucide-react';
import { useBrandConfig } from '../context/BrandConfigContext';
import { buildEverybodyWinsState, readSavedTeamCode } from '../services/everybodyWins';
import { htmlToPlainText } from '../services/brandConfig';
import { prefillEntryCode } from '../services/codePrefill';
import { getFingerprint, getDeviceId } from '../utils/fingerprint';
import API from '../services/api';

// With the default 'daily' frequency, closing the notice hides it for the rest of the
// participant's local day, across browser restarts. It comes back the next day, or straight
// away if the text changes. ('every_visit' doesn't use storage; see closedThisVisit.)
const DISMISS_KEY = 'notice_banner_dismissal';

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function readDismissal() {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY)) || null;
  } catch {
    return null;
  }
}

function writeDismissal(record) {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(record));
  } catch {
    /* storage unavailable (private mode) — dismissal lasts until reload */
  }
}

// All styling lives here so tweaks don't require touching the JSX below.
// The brand color is passed in as the `--nb-primary` CSS custom property.
const STYLES = `
  .nb-bar,
  .nb-bar *,
  .nb-popup,
  .nb-popup * {
    box-sizing: border-box;
  }
  .nb-bar {
    position: relative;
    z-index: 11;
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 38px;
    padding: 6px 8px 6px 12px;
    background-color: var(--nb-primary);
    color: #fff;
    font-size: 0.8rem;
    font-weight: 500;
  }
  .nb-icon {
    flex-shrink: 0;
    opacity: 0.9;
  }
  .nb-thumb {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    border-radius: 4px;
    object-fit: contain;
  }
  .nb-rich a {
    color: inherit;
    text-decoration: underline;
  }
  .nb-viewport {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    container-type: inline-size;
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 16px, #000 calc(100% - 16px), transparent);
            mask-image: linear-gradient(90deg, transparent, #000 16px, #000 calc(100% - 16px), transparent);
  }
  .nb-track {
    display: inline-flex;
    white-space: nowrap;
    animation: nb-scroll var(--nb-duration) linear infinite;
  }
  .nb-viewport:hover .nb-track,
  .nb-viewport:focus-within .nb-track {
    animation-play-state: paused;
  }
  .nb-item {
    flex-shrink: 0;
    /* Each copy spans at least the bar, so short notices scroll in from the right edge */
    min-width: 100cqw;
    padding-right: 64px;
  }
  /* Rich text flattened onto one line: blocks run inline, separated by a bullet */
  .nb-item * {
    display: inline;
    margin: 0;
    padding: 0;
    font-size: inherit;
    line-height: inherit;
  }
  .nb-item > * + *::before,
  .nb-item li + li::before {
    content: '\\00a0\\00a0•\\00a0\\00a0';
    font-weight: normal;
    font-style: normal;
  }
  @keyframes nb-scroll {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  .nb-close {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    cursor: pointer;
  }
  .nb-close:hover,
  .nb-close:focus-visible {
    background: rgba(255, 255, 255, 0.28);
  }

  /* ── Cookie-style pop-up ──
     Styled after the campaign key visual: brand-red card, white diagonal shards in the
     corners, the product image standing on the bottom-left shard with no tile behind it,
     heavy white headline and a white pill button. */
  .nb-popup {
    position: fixed;
    left: 12px;
    right: 12px;
    bottom: 14px;
    z-index: 1040;
    display: flex;
    align-items: center;
    max-width: 380px;
    min-height: 124px;
    margin: 0 auto;
    padding: 16px 42px 16px 122px;
    border-radius: 14px;
    background: var(--nb-primary);
    color: #fff;
    box-shadow: 0 22px 50px rgba(0, 0, 0, 0.38), 0 3px 8px rgba(0, 0, 0, 0.18);
    animation: nb-rise 0.4s cubic-bezier(0.2, 0.9, 0.3, 1.2);
  }
  .nb-popup.nb-popup--no-media {
    min-height: 0;
    padding-left: 88px;
  }
  @media (min-width: 768px) {
    .nb-popup { left: 24px; right: auto; bottom: 24px; width: 380px; margin: 0; }
  }
  /* Shade over the page while the pop-up is up, darkest behind the card. Tapping it never
     dismisses the notice. By default it's purely visual and the page stays usable; with
     block_page it catches every tap/click (and scrolling is locked while it's up). */
  .nb-scrim {
    position: fixed;
    inset: 0;
    z-index: 1039;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0.42) 60%, rgba(0, 0, 0, 0.34));
    pointer-events: none;
    animation: nb-fade 0.4s ease-out;
  }
  .nb-scrim.nb-scrim--blocking {
    pointer-events: auto;
    touch-action: none;
  }
  @keyframes nb-fade {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  /* Background layer, clipped to the card so the shards keep its rounded corners */
  .nb-popup-bg {
    position: absolute;
    inset: 0;
    overflow: hidden;
    border-radius: inherit;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(0, 0, 0, 0.14));
    pointer-events: none;
  }
  .nb-shard {
    position: absolute;
    background: #fff;
  }
  .nb-shard-tr {
    top: 0;
    right: 0;
    width: 96px;
    height: 46px;
    clip-path: polygon(18% 0, 100% 0, 100% 100%);
  }
  .nb-shard-tr2 {
    top: 46px;
    right: 0;
    width: 40px;
    height: 22px;
    clip-path: polygon(100% 0, 100% 100%, 0 0);
    opacity: 0.9;
  }
  .nb-shard-bl {
    bottom: 0;
    left: 0;
    width: 120px;
    height: 58px;
    clip-path: polygon(0 8%, 100% 100%, 0 100%);
  }
  /* Product image: fitted (never cropped), standing on the bottom-left shard */
  .nb-popup-media {
    position: absolute;
    left: 6px;
    bottom: 8px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 112px;
    height: 168px;
    pointer-events: none;
  }
  .nb-popup-media img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 10px 12px rgba(0, 0, 0, 0.35));
    transform: rotate(-5deg);
  }
  /* No image: a white badge with the megaphone */
  .nb-popup-badge {
    position: absolute;
    left: 18px;
    top: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 54px;
    height: 54px;
    border-radius: 50%;
    background: #fff;
    color: var(--nb-primary);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.22);
    transform: translateY(-50%) rotate(-8deg);
  }
  .nb-popup-body {
    position: relative;
    flex: 1;
    min-width: 0;
  }
  .nb-popup-text {
    margin: 0 0 12px;
    font-size: 1.12rem;
    font-weight: 900;
    line-height: 1.1;
    letter-spacing: 0.005em;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
    overflow-wrap: anywhere;
  }
  .nb-popup-text > :first-child { margin-top: 0; }
  .nb-popup-text > :last-child { margin-bottom: 0; }
  .nb-popup-text p,
  .nb-popup-text ul,
  .nb-popup-text ol { margin: 0 0 6px; }
  .nb-popup-text ul,
  .nb-popup-text ol { padding-left: 18px; }
  .nb-popup-text h1,
  .nb-popup-text h2,
  .nb-popup-text h3 { margin: 0 0 6px; font-size: 1.3rem; font-weight: 900; }
  .nb-popup-ok {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 8px 22px;
    border: 0;
    border-radius: 50rem;
    background: #fff;
    color: var(--nb-primary);
    font-size: 0.9rem;
    font-weight: 900;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    cursor: pointer;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.25);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }
  .nb-popup-ok:hover,
  .nb-popup-ok:focus-visible {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.3);
  }
  .nb-popup-ok:active {
    transform: translateY(0);
  }
  .nb-popup-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 10px;
  }
  .nb-popup-no {
    padding: 8px 16px;
    border: 2px solid rgba(255, 255, 255, 0.85);
    border-radius: 50rem;
    background: transparent;
    color: #fff;
    font-size: 0.9rem;
    font-weight: 900;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    cursor: pointer;
    transition: background-color 0.15s ease;
  }
  .nb-popup-no:hover,
  .nb-popup-no:focus-visible {
    background: rgba(255, 255, 255, 0.15);
  }
  .nb-popup .nb-close {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    background: #fff;
    color: var(--nb-primary);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
  .nb-popup .nb-close:hover,
  .nb-popup .nb-close:focus-visible {
    background: #fff;
    transform: scale(1.06);
  }
  @keyframes nb-rise {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .nb-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: nb-spin 0.7s linear infinite;
  }
  @keyframes nb-spin {
    to { transform: rotate(360deg); }
  }

  /* ── Generate Code view (button action 'generate_code') ──
     The card widens into a centred layout: info line, the code in a dashed white tile
     (characters roll like a lottery board before settling), and a copy button. */
  .nb-popup.nb-popup--code {
    padding: 22px 20px 18px;
    text-align: center;
  }
  .nb-popup--code .nb-popup-body {
    animation: nb-fade 0.3s ease-out;
  }
  .nb-code-info {
    margin: 0 28px 12px;
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.25;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
    overflow-wrap: anywhere;
  }
  .nb-code-tile {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    margin: 0 0 14px;
    padding: 12px 44px 12px 12px;
    border: 3px dashed var(--nb-primary);
    border-radius: 10px;
    outline: 3px solid #fff;
    background: #fff;
    color: var(--nb-primary);
    cursor: pointer;
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.25);
    transition: transform 0.15s ease;
  }
  .nb-code-tile:hover,
  .nb-code-tile:focus-visible { transform: translateY(-1px); }
  .nb-code-tile:active { transform: translateY(0) scale(0.99); }
  .nb-code-chars {
    display: flex;
    justify-content: center;
    font-size: clamp(1.25rem, 6.4vw, 1.6rem);
    font-weight: 900;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
    line-height: 1.2;
  }
  .nb-code-slot {
    display: inline-block;
    /*width: 0.8em;*/
    height: 1.2em;
    overflow: hidden;
    text-align: center;
  }
  .nb-code-slot > span {
    display: block;
    animation: nb-roll 0.09s ease-out;
  }
  .nb-code-slot.nb-code-slot--set > span {
    animation: nb-settle 0.32s cubic-bezier(0.3, 1.6, 0.5, 1);
  }
  @keyframes nb-roll {
    from { transform: translateY(-70%); opacity: 0.2; }
    to   { transform: translateY(0); opacity: 1; }
  }
  @keyframes nb-settle {
    0%   { transform: translateY(-60%); }
    100% { transform: translateY(0); }
  }
  .nb-code-copy {
    position: absolute;
    top: 50%;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: var(--nb-primary);
    color: #fff;
    transform: translateY(-50%);
  }
  .nb-popup--code .nb-popup-ok {
    justify-content: center;
    width: 100%;
    padding: 11px 22px;
  }
  .nb-code-error {
    margin: 0 28px 14px;
    font-size: 1rem;
    font-weight: 800;
    line-height: 1.25;
  }
  @media (prefers-reduced-motion: reduce) {
    .nb-popup,
    .nb-scrim,
    .nb-popup--code .nb-popup-body,
    .nb-code-slot > span { animation: none; }
    .nb-viewport { -webkit-mask-image: none; mask-image: none; }
    .nb-track { animation: none; white-space: normal; }
    .nb-item { min-width: 0; padding-right: 0; }
    .nb-item + .nb-item { display: none; }
  }
`;

const ROLL_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ0123456789';
const ROLL_TICK_MS = 55;
// Ticks every character rolls before the first one lands, then the gap between each landing
const ROLL_LEAD = 10;
const ROLL_STAGGER = 3;

/**
 * The generated code, revealed like a lottery board: every character rolls through random
 * letters/digits and they land one after another, left to right. Reduced motion: shown as-is.
 */
const LotteryCode = ({ code }) => {
  const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [tick, setTick] = useState(0);
  const lastTick = ROLL_LEAD + (code.length - 1) * ROLL_STAGGER;
  const done = reduceMotion || tick >= lastTick;

  useEffect(() => {
    if (done) return undefined;
    const timer = setInterval(() => setTick((t) => t + 1), ROLL_TICK_MS);
    return () => clearInterval(timer);
  }, [done]);

  return (
    <span className="nb-code-chars" aria-label={code.split('').join(' ')}>
      {code.split('').map((char, i) => {
        const landed = done || tick >= ROLL_LEAD + i * ROLL_STAGGER;
        const shown = landed ? char : ROLL_CHARS[(i * 7 + tick * 13) % ROLL_CHARS.length];
        return (
          <span key={i} className={`nb-code-slot${landed ? ' nb-code-slot--set' : ''}`} aria-hidden="true">
            {/* A new key restarts the drop-in animation on every change */}
            <span key={landed ? 'set' : tick}>{shown}</span>
          </span>
        );
      })}
    </span>
  );
};

async function copyText(value) {
  try {
    await navigator.clipboard.writeText(value);
    return;
  } catch {
    /* Clipboard API unavailable (http, older WebViews) — fall back to a hidden textarea */
  }
  const area = document.createElement('textarea');
  area.value = value;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  try {
    document.execCommand('copy');
  } catch {
    /* the code is still filled into the form */
  }
  document.body.removeChild(area);
}

/**
 * Site-wide notice configured under Platform Settings → Engagement → Notice in the
 * dashboard, shown either as a scrolling bar at the top of every page ('marquee')
 * or as a cookie-style pop-up at the bottom ('cookie'). Closing it hides it for the
 * rest of the day (see DISMISS_KEY) or, with frequency 'every_visit', until the next visit.
 */
const NoticeBanner = () => {
  const brand = useBrandConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { enabled, text, style, imageUrl, ctaText, declineText, ctaAction, codeInfoText, frequency, blockPage } = brand?.noticeBanner ?? {};
  // Generate Code: 'idle' → 'loading' → 'code' (or 'error' with a message)
  const [codeFlow, setCodeFlow] = useState({ status: 'idle', code: '', message: '' });
  const [copied, setCopied] = useState(false);
  const [dismissal, setDismissal] = useState(readDismissal);
  // 'every_visit': closing only lasts until the portal is next opened or reloaded (component state,
  // so it stays closed while moving between pages within the visit)
  const [closedThisVisit, setClosedThisVisit] = useState('');
  // A broken image URL falls back to the default megaphone icon
  const [failedImage, setFailedImage] = useState('');
  const popupRef = useRef(null);
  const ctaRef = useRef(null);

  // Brand config re-fetches when the tab regains focus; keep in step with storage
  // (another tab may have closed the notice)
  useEffect(() => {
    const stored = readDismissal();
    setDismissal((prev) => (prev?.text === stored?.text && prev?.date === stored?.date ? prev : stored));
  }, [brand]);

  // The date is checked on every render, so a tab left open overnight shows it again
  // on the next config refresh
  const everyVisit = frequency === 'every_visit';
  const dismissed = everyVisit
    ? closedThisVisit === text
    : dismissal?.text === text && dismissal?.date === todayKey();
  // Landing page only (FAQ, T&Cs, Everybody Wins etc. never show it, on refresh or Back). The
  // component stays mounted across routes so an 'every_visit' close still holds on return to '/'
  const onLandingPage = location.pathname.replace(/\/+$/, '') === '';
  const visible = !!(enabled && text && !dismissed && onLandingPage);
  // Block Page (cookie pop-up only): the page behind can't be scrolled or used until it's closed
  const blocking = visible && style === 'cookie' && !!blockPage;

  useEffect(() => {
    if (!blocking) return undefined;
    const { body, documentElement: root } = document;
    const previous = { body: body.style.overflow, root: root.style.overflow };
    body.style.overflow = 'hidden';
    root.style.overflow = 'hidden';
    // Keyboard users land on the button rather than behind the shade
    const previouslyFocused = document.activeElement;
    ctaRef.current?.focus({ preventScroll: true });
    return () => {
      body.style.overflow = previous.body;
      root.style.overflow = previous.root;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus({ preventScroll: true });
    };
  }, [blocking]);

  // The card swaps its content for the code (or an error): keep focus on its button
  useEffect(() => {
    if (codeFlow.status === 'code' || codeFlow.status === 'error') ctaRef.current?.focus({ preventScroll: true });
  }, [codeFlow.status]);

  if (!visible) return null;

  const close = () => {
    setCodeFlow({ status: 'idle', code: '', message: '' });
    setCopied(false);
    if (everyVisit) {
      setClosedThisVisit(text);
      return;
    }
    const record = { text, date: todayKey() };
    writeDismissal(record);
    setDismissal(record);
  };

  // Same fallback as the pause popup: theme.primaryColor isn't always kept in sync with the skin
  const primaryColor = brand?.theme?.primaryColor || '#de0b1c';
  // Roughly constant reading speed regardless of message length
  const duration = `${Math.max(12, htmlToPlainText(text).length * 0.22)}s`;
  const image = imageUrl && imageUrl !== failedImage ? imageUrl : '';
  const onImageError = () => setFailedImage(imageUrl);
  // "Open Everybody Wins" needs the feature on; otherwise the button just closes the notice
  const opensEverybodyWins = ctaAction === 'everybody_wins' && !!brand?.everybody_wins?.enabled;
  const generatesCode = ctaAction === 'generate_code';
  const generateCode = async () => {
    setCodeFlow({ status: 'loading', code: '', message: '' });
    try {
      const res = await API.notice.generateCode({ fingerprint: await getFingerprint(), deviceId: getDeviceId() });
      const code = res?.data?.code;
      if (!code) throw new Error('No codes are available right now. Please try again later.');
      setCodeFlow({ status: 'code', code, message: '' });
    } catch (err) {
      setCodeFlow({ status: 'error', code: '', message: err?.message || 'Something went wrong. Please try again.' });
    }
  };
  // Copy, show the tick for a moment, then close and fill the entry form's code field
  const copyAndContinue = async () => {
    if (copied) return;
    const { code } = codeFlow;
    await copyText(code);
    setCopied(true);
    setTimeout(() => {
      close();
      prefillEntryCode(code);
      if (location.pathname !== '/') navigate('/');
    }, 650);
  };
  const onCta = () => {
    if (generatesCode) {
      generateCode();
      return;
    }
    close();
    if (opensEverybodyWins) {
      // Their own club if they've entered before, otherwise a random club
      navigate('/everybody-wins', {
        state: { ...buildEverybodyWinsState(brand, { teamCode: readSavedTeamCode(), randomTeamFallback: true }), fromNotice: true }
      });
    }
  };
  // While blocking, Tab cycles within the pop-up instead of reaching the page behind it
  const trapFocus = (event) => {
    if (event.key !== 'Tab' || !popupRef.current) return;
    const focusable = [...popupRef.current.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')];
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  // Rich text is authored by super admins in Platform Settings, as with the T&Cs and FAQ content
  const html = { __html: text };
  const showingCode = codeFlow.status === 'code' || codeFlow.status === 'error';

  if (style === 'cookie') {
    return (
      <>
        <div className={`nb-scrim${blocking ? ' nb-scrim--blocking' : ''}`} aria-hidden="true" />
        <div
          ref={popupRef}
          className={`nb-popup${showingCode ? ' nb-popup--code' : image ? '' : ' nb-popup--no-media'}`}
          style={{ '--nb-primary': primaryColor }}
          role={blocking ? 'dialog' : 'region'}
          aria-modal={blocking || undefined}
          aria-label="Notice"
          onKeyDown={blocking ? trapFocus : undefined}
        >
          <style>{STYLES}</style>
          <div className="nb-popup-bg" aria-hidden="true">
            <span className="nb-shard nb-shard-tr" />
            <span className="nb-shard nb-shard-tr2" />
            <span className="nb-shard nb-shard-bl" />
          </div>
          {showingCode ? null : image ? (
            <div className="nb-popup-media" aria-hidden="true">
              <img src={image} alt="" onError={onImageError} />
            </div>
          ) : (
            <div className="nb-popup-badge" aria-hidden="true">
              <Megaphone size={24} />
            </div>
          )}
          {codeFlow.status === 'code' ? (
            <div className="nb-popup-body">
              <p className="nb-code-info">{codeInfoText || 'Tap to copy your participation code'}</p>
              <button type="button" className="nb-code-tile" onClick={copyAndContinue} aria-label={`Copy code ${codeFlow.code}`}>
                <LotteryCode code={codeFlow.code} />
                <span className="nb-code-copy" aria-hidden="true">
                  {copied ? <Check size={18} strokeWidth={3} /> : <Copy size={16} strokeWidth={2.5} />}
                </span>
              </button>
              <button type="button" className="nb-popup-ok" onClick={copyAndContinue} ref={ctaRef}>
                {copied ? 'Copied!' : 'Copy and Continue'}
              </button>
            </div>
          ) : codeFlow.status === 'error' ? (
            <div className="nb-popup-body">
              <p className="nb-code-error" role="alert">{codeFlow.message}</p>
              <button type="button" className="nb-popup-ok" onClick={close} ref={ctaRef}>
                OK
              </button>
            </div>
          ) : (
            <div className="nb-popup-body">
              <div className="nb-popup-text nb-rich" dangerouslySetInnerHTML={html} />
              <div className="nb-popup-actions">
                <button type="button" className="nb-popup-ok" onClick={onCta} ref={ctaRef} disabled={codeFlow.status === 'loading'}>
                  {codeFlow.status === 'loading' ? (
                    <>
                      <span className="nb-spinner" aria-hidden="true" /> Generating…
                    </>
                  ) : (
                    <>
                      {ctaText || 'Got it'}
                      {(opensEverybodyWins || generatesCode) && <ChevronRight size={16} strokeWidth={3} aria-hidden="true" />}
                    </>
                  )}
                </button>
                {/* Optional second button ("No"): only shown when the dashboard gives it text; it just closes */}
                {declineText && codeFlow.status !== 'loading' && (
                  <button type="button" className="nb-popup-no" onClick={close}>
                    {declineText}
                  </button>
                )}
              </div>
            </div>
          )}
          <button type="button" className="nb-close" onClick={close} aria-label="Close notice">
            <X size={14} strokeWidth={3} />
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="nb-bar" style={{ '--nb-primary': primaryColor, '--nb-duration': duration }} role="region" aria-label="Notice">
      <style>{STYLES}</style>
      {image ? (
        <img className="nb-thumb" src={image} alt="" onError={onImageError} />
      ) : (
        <Megaphone size={16} className="nb-icon" aria-hidden="true" />
      )}
      <div className="nb-viewport" tabIndex={0}>
        {/* Text is repeated so the loop is seamless; the copy is hidden from screen readers */}
        <div className="nb-track">
          <span className="nb-item nb-rich" dangerouslySetInnerHTML={html} />
          <span className="nb-item nb-rich" aria-hidden="true" dangerouslySetInnerHTML={html} />
        </div>
      </div>
      <button type="button" className="nb-close" onClick={close} aria-label="Close notice">
        <X size={16} />
      </button>
    </div>
  );
};

export default NoticeBanner;
